<template>
  <v-app>
    <!-- Search Panel -->
    <v-container fluid>
      <v-expansion-panels v-model="panel">
        <v-expansion-panel>
          <v-expansion-panel-title
            color="blue-grey-lighten-4"
            class="panel-header"
          >
            Search Finishing Records
          </v-expansion-panel-title>

          <v-expansion-panel-text>
            <v-form @submit.prevent="submit">
              <v-row class="px-2 pt-4">
                <v-col md="3">
                  <v-date-input
                    input-format="dd/mm/yyyy"
                    label="From Date"
                    density="compact"
                    variant="outlined"
                    bg-color="grey-lighten-4"
                    prepend-icon=""
                    v-model="from_date.value.value"
                    clearable
                  />
                </v-col>

                <v-col md="3">
                  <v-date-input
                    input-format="dd/mm/yyyy"
                    label="To Date"
                    density="compact"
                    variant="outlined"
                    prepend-icon=""
                    bg-color="grey-lighten-4"
                    v-model="to_date.value.value"
                    clearable
                  />
                </v-col>

                <v-col md="2" class="d-flex align-center">
                  <v-btn type="submit" color="primary">Search</v-btn>
                </v-col>
              </v-row>
            </v-form>
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-container>

    <!-- Finishing Records List -->
    <v-container fluid style="margin-top:-20px">
      <v-expansion-panels v-model="panel">
        <v-expansion-panel>
          <v-expansion-panel-title
            color="blue-grey-lighten-4"
            class="panel-header"
          >
            Finishing Records List
          </v-expansion-panel-title>

          <v-expansion-panel-text>
            <v-row class="py-2">
              <v-col md="4">
                <v-text-field
                  v-model="search"
                  label="Search"
                  density="compact"
                  variant="outlined"
                  bg-color="grey-lighten-4"
                  append-inner-icon="mdi-magnify"
                  clearable
                />
              </v-col>
            </v-row>

            <v-data-table
              :headers="headers"
              :items="finishingList"
              :search="search"
              :loading="loading"
              density="compact"
            >
              <template #item.index="{ index }">
                {{ index + 1 }}
              </template>

              <template #item.status="{ item }">
                <v-chip
                  size="small"
                  :color="item.status === 1 ? 'success' : 'grey'"
                >
                  {{ item.status === 1 ? 'Active' : 'Inactive' }}
                </v-chip>
              </template>

              <template #item.finishing_date="{ item }">
                {{ formatDateView(item.finishing_date) }}
              </template>

              <template #item.actions="{ item }">
                <div class="d-flex align-center" style="gap: 6px;">
                  <v-tooltip text="Edit">
                    <template v-slot:activator="{ props }">
                      <v-btn
                        v-bind="props"
                        icon="mdi-pencil"
                        size="small"
                        variant="text"
                        color="primary"
                        @click="goToEdit(item.id)"
                        v-if="$updateAccess($route.fullPath)"
                      />
                    </template>
                  </v-tooltip>
                  <v-tooltip text="View Details">
                    <template v-slot:activator="{ props }">
                      <v-btn
                        v-bind="props"
                        icon="mdi-eye"
                        size="small"
                        variant="text"
                        color="info"
                        @click="viewDetails(item)"
                        v-if="$viewAccess($route.fullPath)"
                      />
                    </template>
                  </v-tooltip>
                </div>
              </template>
            </v-data-table>
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-container>

    <!-- Details Dialog -->
    <v-dialog v-model="detailsDialog" max-width="900px">
      <v-card>
        <v-card-title class="bg-blue-grey-lighten-4">
          Finishing Details - {{ selectedItem?.code }}
        </v-card-title>

        <v-card-text>
          <v-row>
            <v-col md="6"><b>PO:</b> {{ selectedItem?.production_order_id }}</v-col>
            <v-col md="6"><b>Branch:</b> {{ selectedItem?.branch_id }}</v-col>
            <v-col md="6"><b>Finishing Date:</b> {{ formatDateView(selectedItem?.finishing_date) }}</v-col>
            <v-col md="6"><b>Remarks:</b> {{ selectedItem?.remarks || '-' }}</v-col>
          </v-row>

          <v-divider class="my-4" />

          <v-table density="compact">
            <thead>
              <tr>
                <th>#</th>
                <th>Color</th>
                <th>Size</th>
                <th class="text-right">Finished Qty</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(d, i) in selectedItem?.details || []"
                :key="i"
              >
                <td>{{ i + 1 }}</td>
                <td>{{ d.color?.name }}</td>
                <td>{{ d.size?.name }}</td>
                <td class="text-right">{{ d.finished_qty }}</td>
              </tr>
            </tbody>
          </v-table>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn variant="tonal" @click="detailsDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-app>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useStore } from "vuex";
import { useForm, useField } from "vee-validate";
import * as yup from "yup";
import { useRouter } from "vue-router";
import { formatDateView, formatDateDash } from "@/plugins/helpers";

const store = useStore();

const panel = ref([0]);
const search = ref("");
const loading = ref(false);
const detailsDialog = ref(false);
const selectedItem = ref(null);
const router = useRouter();

const headers = [
  { title: "#", key: "index", sortable: false },
  { title: "Finishing Code", key: "code" },
  { title: "PO", key: "production.code" },
  { title: "Finishing Date", key: "finishing_date" },
  { title: "Status", key: "status" },
  { title: "Actions", key: "actions", sortable: false },
];

const { handleSubmit } = useForm({
  validationSchema: yup.object({
    from_date: yup.date().nullable(),
    to_date: yup.date().nullable(),
  }),
});

const from_date = useField("from_date");
const to_date = useField("to_date");

const submit = handleSubmit(() => {
  loadList();
});

const finishingList = computed(() => store.getters.getProductionFinishingList || []);
const loadList = () => {
  loading.value = true;
  store.dispatch("getProductionFinishingList").finally(() => {
    loading.value = false;
  });
};

const viewDetails = (item) => {
  router.push({ name: "view-finishing", params: { id: item.id } });
  // selectedItem.value = item;
  // detailsDialog.value = true;
};

const goToEdit = (id) => {
  router.push({ name: "finishing-entry", params: { id } });
};

onMounted(() => {
  loadList();
});
</script>
